import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImpactRecordCard } from "@/components/impact-record-card";
import { ImpactProjectDialog } from "@/components/impact-project-dialog";
import { CATEGORIES, type Category } from "@/lib/categories";
import {
  IMPACT_STATUSES,
  IMPACT_STATUS_META,
  fetchImpactCapability,
  fetchImpactProjects,
  fetchMetricsForProjects,
  type ImpactProject,
  type ImpactProjectMetrics,
  type ImpactStatus,
} from "@/lib/impact-projects";
import { Landmark, Loader2, Plus, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const TITLE = "Public works on Xamseen — projects, responses and results";
const DESCRIPTION =
  "A public record of projects, works and responses published by verified offices and organisations, with a timeline from the original problem through to community verification.";

export const Route = createFileRoute("/impact/projects/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/impact/projects" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/impact/projects" }],
  }),
  component: ImpactProjectsPage,
});

function ImpactProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ImpactProject[]>([]);
  const [metrics, setMetrics] = useState<Record<string, ImpactProjectMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ImpactStatus | "all">("all");
  const [category, setCategory] = useState<Category | "all">("all");
  const [term, setTerm] = useState("");
  const [capability, setCapability] = useState<{ canPublish: boolean; organization: string | null; city: string | null; country: string | null }>({
    canPublish: false, organization: null, city: null, country: null,
  });
  const [dialog, setDialog] = useState(false);

  useEffect(() => {
    fetchImpactCapability(user?.id).then(setCapability);
  }, [user?.id]);

  const load = () => {
    setLoading(true);
    fetchImpactProjects({ status, category })
      .then(async (rows) => {
        setProjects(rows);
        setMetrics(await fetchMetricsForProjects(rows.map((r) => r.id)));
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [status, category]);

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      [p.title, p.summary, p.organization, p.location_name, p.city, p.country]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [projects, term]);

  return (
    <AppShell
      allowAnonymous
      rightRail={
        <div className="space-y-4 py-3">
          <div className="panel px-4 py-4">
            <h3 className="font-display text-[15px] font-semibold">How this record works</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Only verified offices and organisations can publish here. Every number on a project is counted by Xamseen
              from what actually happened — reports linked to it, reports resolved, updates posted and residents who
              confirmed the finished work.
            </p>
          </div>
          <div className="panel px-4 py-4">
            <h3 className="font-display text-[15px] font-semibold">The lifecycle</h3>
            <ol className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              {IMPACT_STATUSES.map((s) => (
                <li key={s} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: IMPACT_STATUS_META[s].color }} />
                  <span><b className="font-medium text-foreground">{IMPACT_STATUS_META[s].label}</b> — {IMPACT_STATUS_META[s].note}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <Landmark className="h-3.5 w-3.5" /> Public works
            </p>
            <h1 className="mt-1.5 font-display text-2xl font-bold tracking-tight sm:text-3xl">Projects and responses</h1>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              What offices and organisations said they would do, what they have done, and what residents say about it.
            </p>
          </div>
          {capability.canPublish && user ? (
            <Button onClick={() => setDialog(true)}>
              <Plus className="mr-2 h-4 w-4" /> New project
            </Button>
          ) : null}
        </header>

        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search by title, place or organisation"
              className="pl-9"
              aria-label="Search projects"
            />
          </div>

          <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
            <Chip active={status === "all"} onClick={() => setStatus("all")}>All stages</Chip>
            {IMPACT_STATUSES.map((s) => (
              <Chip key={s} active={status === s} onClick={() => setStatus(s)}>{IMPACT_STATUS_META[s].short}</Chip>
            ))}
          </div>

          <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
            <Chip active={category === "all"} onClick={() => setCategory("all")}>Everything</Chip>
            {(Object.keys(CATEGORIES) as Category[]).map((c) => (
              <Chip key={c} active={category === c} onClick={() => setCategory(c)}>{CATEGORIES[c].label}</Chip>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="panel px-6 py-14 text-center">
            <h2 className="font-display text-lg font-bold">Nothing published here yet</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              When a verified office publishes a project, it appears here with its own timeline and the reports that
              prompted it.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {visible.map((p) => (
              <ImpactRecordCard key={p.id} project={p} metrics={metrics[p.id]} />
            ))}
          </div>
        )}
      </div>

      {capability.canPublish && user ? (
        <ImpactProjectDialog
          open={dialog}
          onOpenChange={setDialog}
          userId={user.id}
          defaults={capability}
          onSaved={() => load()}
        />
      ) : null}
    </AppShell>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors",
        active ? "border-transparent bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-surface",
      )}
    >
      {children}
    </button>
  );
}
